import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { ChartColumnBig } from "lucide-react";
import React from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

const SalesForecastChart: React.FC = () => {
    const [graphType, setGraphType] = React.useState<'week' | 'month'>('week');

    // Weekly data (7 days)
    const weeklyData = [
        { day: "Monday", actual: 45000, forecast: 48000 },
        { day: "Tuesday", actual: 42000, forecast: 44000 },
        { day: "Wednesday", actual: 52000, forecast: 53000 },
        { day: "Thursday", actual: 68000, forecast: 72000 },
        { day: "Friday", actual: 65000, forecast: 70000 },
        { day: "Saturday", actual: 58000, forecast: 62000 },
        { day: "Sunday", actual: 72000, forecast: 75000 }
    ];

    // Monthly data (30 days)
    const monthlyData = [
        { day: "Week 1", actual: 180000, forecast: 185000 },
        { day: "Week 2", actual: 195000, forecast: 200000 },
        { day: "Week 3", actual: 210000, forecast: 215000 },
        { day: "Week 4", actual: 205000, forecast: 210000 }
    ];

    const currentData = graphType === 'week' ? weeklyData : monthlyData;

    return (
        <div className="flex flex-col items-start justify-start w-full border border-gray-200 rounded-xl">
            <div className="flex flex-row items-center justify-between w-full py-2 px-4">
                <div className="flex flex-row items-center justify-start gap-x-2">
                    <ChartColumnBig className="text-gray-500 h-5 w-5"/>
                    <span className="text-sm text-gray-500 font-normal">Sales vs Forecast</span>
                </div>
                <div className="flex flex-row items-center justify-end gap-x-2">
                    <Button 
                        onClick={() => setGraphType("week")} 
                        variant={graphType === "week" ? "outline" : "ghost"} 
                        className={cn("text-sm p-2 rounded-lg", graphType === "week" ? "bg-gray-100" : "bg-white")}
                    >
                        Week
                    </Button>
                    <Button 
                        onClick={() => setGraphType("month")} 
                        variant={graphType === "month" ? "outline" : "ghost"} 
                        className={cn("text-sm p-2 rounded-lg", graphType === "month" ? "bg-gray-100" : "bg-white")}
                    >
                        Month
                    </Button>
                </div>
            </div>
            <div className="w-full border-[0.2px] border-gray-300" />
            <div className="w-full p-6">
                <span className="text-sm text-gray-500 mb-4 block">
                    Weekly revenue performance against AI predictions
                </span>
                <div className="w-full h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={currentData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                </linearGradient>
                            </defs>
                            <CartesianGrid 
                                strokeDasharray="0" 
                                stroke="#e5e7eb" 
                                vertical={true}
                                horizontal={true}
                            />
                            <XAxis 
                                dataKey="day" 
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: '#6b7280' }}
                                dy={10}
                            />
                            <YAxis 
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: '#6b7280' }}
                                tickFormatter={(value) => `${value / 1000}k`}
                                dx={-10}
                            />
                            <Tooltip 
                                contentStyle={{ 
                                    backgroundColor: 'white', 
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    padding: '8px 12px'
                                }}
                                formatter={(value: number) => [`Dh ${value.toLocaleString()}`, '']}
                                labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
                            />
                            <Area 
                                type="linear" 
                                dataKey="forecast" 
                                stroke="#1f2937" 
                                strokeWidth={2}
                                fill="transparent"
                                dot={false}
                            />
                            <Area 
                                type="linear" 
                                dataKey="actual" 
                                stroke="#3b82f6" 
                                strokeWidth={2.5}
                                fill="url(#colorActual)"
                                dot={false}
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}

export default SalesForecastChart;
